import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
} from '@azure/msal-browser';
import { addHours } from 'date-fns';
import { filter, finalize, of, switchMap, tap, throttleTime } from 'rxjs';
import { v7 as uuid } from 'uuid';
import { APP_NAME } from '../app-token';
import { environment } from '../environment';
@Injectable({
  providedIn: 'root',
})
export class AuthCookieService {
  private msalBroadcastService = inject(MsalBroadcastService);
  private http = inject(HttpClient);
  private env = inject(environment);
  private appName = inject(APP_NAME);
  private b2cCookieName = `SharedB2C${this.env.name}`;
  private loginB2CInProgress = false;

  /*
  Store firstname/lastname/email in cookie since the edit-flow won't update idtoken in all apps

  Only store tfp if new cookie (to avoid storing edit-flow tfps). Default to signInPhone if the tfp is signUp
  */
  private setSharedB2C(msg: EventMessage): void {
    const tfp = (
      (msg.payload as AuthenticationResult).idTokenClaims as IdTokenClaims
    )['tfp'];
    const sub = (
      (msg.payload as AuthenticationResult).idTokenClaims as IdTokenClaims
    )['sub'];
    const cookie = this.getCookie();
    const cookieValue = { sub } as B2CCookie;
    if (cookie) {
      cookieValue.tfp = cookie.tfp;
      cookieValue.sites = cookie.sites;
      cookieValue.sessionId = cookie.sessionId;
    } else {
      cookieValue.tfp =
        tfp === this.env.b2cMsalConfig?.policies.signUp.name
          ? this.env.b2cMsalConfig?.policies.signInPhone.name
          : tfp;
      cookieValue.sites = [];
    }
    this.setCookie(cookieValue);
  }

  public loginB2C() {
    if (this.loginB2CInProgress) {
      return of(undefined);
    }
    this.loginB2CInProgress = true;
    return this.http
      .get<{
        token: string;
      }>(`${this.env.etjansterCommonApiUrl}/account/loginb2c`)
      .pipe(finalize(() => (this.loginB2CInProgress = false)));
  }

  private setCookie(cookieValue: B2CCookie) {
    const gmtDateString = addHours(new Date(), 1).toUTCString();

    let cookie =
      `${this.b2cCookieName}=` +
      `${encodeURIComponent(JSON.stringify(cookieValue))};` +
      'path=/;';
    if (this.env.cookieDomain !== 'localhost') {
      cookie += `domain=${this.env.cookieDomain};`;
    }
    cookie += `expires=${gmtDateString}`;

    document.cookie = cookie;
  }

  public getCookie(): B2CCookie | undefined {
    const nameLenPlus = this.b2cCookieName.length + 1;
    const cookie =
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => {
          return (
            cookie.slice(0, Math.max(0, nameLenPlus)) ===
            `${this.b2cCookieName}=`
          );
        })
        .map((cookie) => {
          return decodeURIComponent(cookie.slice(Math.max(0, nameLenPlus)));
        })[0] || undefined;
    return cookie ? JSON.parse(cookie) : undefined;
  }

  public getSessionId() {
    const cookieValue = this.getCookie();
    if (!cookieValue) {
      return;
    }
    if (cookieValue.sessionId) {
      return cookieValue.sessionId;
    }

    const sessionId = uuid();
    cookieValue.sessionId = sessionId;
    this.setCookie(cookieValue);
    return sessionId;
  }

  public addSite(): void {
    const cookie = this.getCookie();
    if (!cookie) {
      return;
    }
    const sites = cookie.sites ?? [];
    if (cookie.sites && !cookie.sites.includes(this.appName)) {
      sites.push(this.appName);
    }
    cookie.sites = sites;
    this.setCookie(cookie);
  }

  public removeAllSites(): void {
    const cookie = this.getCookie();
    if (!cookie) {
      return;
    }
    cookie.sites = [];
    this.setCookie(cookie);
  }

  private isSiteLoggedIn(site: string) {
    const cookie = this.getCookie();
    if (!cookie) {
      return false;
    }

    return cookie.sites && cookie.sites.includes(site);
  }

  public isCurrentSiteLoggedIn() {
    return this.isSiteLoggedIn(this.appName);
  }

  public getB2CCookieValue<K extends keyof B2CCookie>(
    value: K,
  ): B2CCookie[K] | undefined {
    const cookie = this.getCookie();
    return cookie ? cookie[value] : undefined;
  }

  public deleteSharedCookie() {
    const now = new Date();
    now.setSeconds(now.getSeconds() + 1);
    let cookie = `${this.b2cCookieName}=;` + 'path=/;';

    if (this.env.cookieDomain !== 'localhost') {
      cookie += `domain=${this.env.cookieDomain};`;
    }
    cookie += `expires=${now.toUTCString()}`;

    document.cookie = cookie;
  }

  public tokenSuccess() {
    return this.msalBroadcastService.msalSubject$.pipe(
      filter(
        (msg) =>
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          msg.eventType === EventType.LOGIN_SUCCESS,
      ),
      // Update cookie tfp/exp max 1 time per second
      throttleTime(1000),
      tap((msg: EventMessage) => {
        this.setSharedB2C(msg);
      }),
      filter(() => {
        const storedReturnUrl = decodeURIComponent(
          localStorage.getItem('returnUrl') ?? '',
        );
        // Don't set sharedauth if on login-callback since activeaccount haven't been set yet
        // Don't set sharedauth if on public/registration or returnurl on public/registration
        // We don't want to let loginb2c-call create the user
        return !(
          location.href.includes('login-callback') ||
          location.href.includes('public/registration') ||
          storedReturnUrl.includes('public/registration')
        );
      }),
      // Update cookie jwt max 1 time per 5 minutes
      throttleTime(5 * 60 * 1000),
      switchMap(() => this.loginB2C()),
    );
  }
}

export interface B2CCookie {
  tfp?: string;
  sub: string;
  sites: string[];
  sessionId: string;
}
